import { useToast } from '@chakra-ui/react';
import {
  FinalizedPaymentProofItemDto,
  ManualOverride,
  OcrDisplayErrorCode,
  OcrItemDto,
  SubmitPaymentProofsRequest,
} from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import type { OcrPaymentProofRefetchInterval } from '@diamond/sol-buyer-context';
import {
  FINALIZED_PAYMENT_PROOF_QUERY_KEY,
  OCR_PAYMENT_PROOF_QUERY_KEY,
  useDeleteOcrPaymentProof,
  useFinalizedPaymentProofs,
  useOcrPaymentProofItems,
  useSubmitOcrPaymentProofs,
  useUploadOcrPaymentProof,
} from '@diamond/sol-buyer-context';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';

import { OcrCardState, OcrCardViewModel } from './types';

const OCR_POLLING_INTERVAL_MS = 3000;
const OCR_POLLING_TIMEOUT_MS = 30_000;

function hasManualInputFields(state: OcrCardState): boolean {
  return (
    state === 'success' || state === 'manual_fallback' || state === 'duplicate'
  );
}

function isManualFallbackCode(code: OcrDisplayErrorCode | undefined): boolean {
  return (
    code === 'OCR_UNREADABLE' ||
    code === 'OCR_UNAVAILABLE' ||
    code === 'OCR_TIMEOUT' ||
    code === 'PAYMENT_PROOF_AMOUNT_MISMATCH' ||
    code === 'RECIPIENT_MISMATCH'
  );
}

function deriveCardState(item: OcrItemDto): OcrCardState {
  const code: OcrDisplayErrorCode | undefined = item.display_error?.code;
  if (code === 'DUPLICATE_PAYMENT_PROOF') return 'duplicate';
  if (isManualFallbackCode(code)) return 'manual_fallback';
  if (code !== undefined) return 'blocked';
  if (item.ocr_result !== null) {
    return 'success';
  }
  return 'analyzing';
}

interface UsePaymentProofOcrFormParams {
  accessToken: string;
  orderPaymentId: string;
  onSuccess?: () => void;
}

interface ManualOverridesFormData {
  manual_overrides: ManualOverride[];
}

interface UsePaymentProofOcrFormResult {
  // Data
  cards: OcrCardViewModel[];
  submittedProofs: FinalizedPaymentProofItemDto[];

  // Loading states
  isLoadingItems: boolean;
  isLoadingSubmittedProofs: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  isSubmitPending: boolean;
  isSubmitting: boolean;
  isAnalyzingOcr: boolean;
  isPollingTimedOut: boolean;

  // Submit availability
  canSubmit: boolean;

  // Form (for manual_overrides)
  form: UseFormReturn<ManualOverridesFormData>;

  // Actions
  uploadFiles: (files: File[]) => Promise<void>;
  deleteFile: (ocrJobId: string) => Promise<void>;
  submitProof: () => Promise<void>;
  resetPollingTimer: () => void;
}

export const usePaymentProofOcrForm = ({
  accessToken,
  orderPaymentId,
  onSuccess,
}: UsePaymentProofOcrFormParams): UsePaymentProofOcrFormResult => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const [isUploadProcessing, setIsUploadProcessing] = useState(false);
  const pollingStartRef = useRef<number | null>(null);

  const form = useForm<ManualOverridesFormData>({
    defaultValues: {
      manual_overrides: [],
    },
  });

  const manualOverrides = useWatch({
    control: form.control,
    name: 'manual_overrides',
  });

  const uploadMutation = useUploadOcrPaymentProof(accessToken);
  const deleteMutation = useDeleteOcrPaymentProof(accessToken, orderPaymentId);
  const submitMutation = useSubmitOcrPaymentProofs(accessToken, orderPaymentId);

  useEffect(() => {
    setIsPollingTimedOut(false);
    pollingStartRef.current = null;
    form.reset({ manual_overrides: [] });
  }, [form, orderPaymentId]);

  const getPollingInterval = useCallback<
    Exclude<NonNullable<OcrPaymentProofRefetchInterval>, number | false>
  >(
    (query) => {
      if (isPollingTimedOut) return false;

      const hasAnalyzingItem =
        query.state.data?.items.some((item) => {
          return deriveCardState(item) === 'analyzing';
        }) ?? false;

      if (!hasAnalyzingItem) return false;

      if (pollingStartRef.current === null) {
        pollingStartRef.current = Date.now();
      }

      if (Date.now() - pollingStartRef.current >= OCR_POLLING_TIMEOUT_MS) {
        setIsPollingTimedOut(true);
        return false;
      }

      return OCR_POLLING_INTERVAL_MS;
    },
    [isPollingTimedOut]
  );

  const { data, isLoading: isLoadingItems } = useOcrPaymentProofItems(
    orderPaymentId,
    accessToken,
    {
      enabled: !!orderPaymentId,
      refetchInterval: getPollingInterval,
    }
  );
  const { data: finalizedData, isLoading: isLoadingSubmittedProofs } =
    useFinalizedPaymentProofs(orderPaymentId, accessToken, {
      enabled: !!orderPaymentId,
    });
  const submittedProofs = finalizedData?.items ?? [];

  // Build card view models from query data
  const cards: OcrCardViewModel[] = useMemo(() => {
    const queryItems = data?.items ?? [];
    return queryItems.map((item) => {
      return {
        item,
        state: deriveCardState(item),
      };
    });
  }, [data]);

  const isAnalyzingOcr = cards.some((c) => c.state === 'analyzing');

  useEffect(() => {
    if (!isAnalyzingOcr) {
      pollingStartRef.current = null;
    }
  }, [isAnalyzingOcr]);

  // Sync manual_overrides form entries with OCR cards that need manual input.
  useEffect(() => {
    const manualInputItems =
      data?.items.filter((item) => {
        return hasManualInputFields(deriveCardState(item));
      }) ?? [];

    const currentOverrides = form.getValues('manual_overrides');

    const reconciled = manualInputItems.map((item) => {
      const existing = currentOverrides.find((o) => o.ocr_job_id === item.id);
      const amount =
        existing?.amount ??
        item.manual_override?.amount ??
        item.effective_result?.amount ??
        item.ocr_result?.amount ??
        undefined;
      const senderBank =
        existing?.sender_bank ??
        item.manual_override?.sender_bank ??
        item.effective_result?.sender_bank ??
        item.ocr_result?.sender_bank ??
        '';
      const senderAccountName =
        existing?.sender_account_name ??
        item.manual_override?.sender_account_name ??
        item.effective_result?.sender_account_name ??
        item.ocr_result?.sender_account_name ??
        '';

      return {
        ...(amount !== undefined ? { amount } : {}),
        ocr_job_id: item.id,
        sender_account_name: senderAccountName,
        sender_bank: senderBank,
      };
    });

    const isSame =
      currentOverrides.length === reconciled.length &&
      reconciled.every(
        (r, i) =>
          currentOverrides[i]?.ocr_job_id === r.ocr_job_id &&
          currentOverrides[i]?.amount === r.amount &&
          currentOverrides[i]?.sender_bank === r.sender_bank &&
          currentOverrides[i]?.sender_account_name === r.sender_account_name
      );

    if (!isSame) {
      form.setValue('manual_overrides', reconciled);
    }
  }, [data?.items, form]);

  const isUploading = uploadMutation.isPending || isUploadProcessing;
  const isDeleting = deleteMutation.isPending;
  const isSubmitPending = submitMutation.isPending;
  const isSubmitting = isSubmitPending || isUploading || isDeleting;

  const canSubmit = useMemo(() => {
    if (isSubmitting) return false;
    if (cards.length === 0) return false;
    if (cards.some((c) => c.state === 'analyzing' || c.state === 'blocked'))
      return false;

    const hasIncompleteManual = cards.some((c) => {
      if (!hasManualInputFields(c.state)) return false;
      const override = manualOverrides.find((o) => o.ocr_job_id === c.item.id);
      return (
        override?.amount === undefined ||
        !override.sender_bank ||
        !override.sender_account_name
      );
    });

    return !hasIncompleteManual;
  }, [isSubmitting, cards, manualOverrides]);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      setIsUploadProcessing(true);
      try {
        await uploadMutation.mutateAsync({ orderPaymentId, files });
        // Reset polling timer so a new 30s budget starts for freshly uploaded files
        pollingStartRef.current = null;
        setIsPollingTimedOut(false);
        await queryClient.invalidateQueries({
          queryKey: [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Terjadi kesalahan';
        showToast(toast, 'error', message);
      } finally {
        setIsUploadProcessing(false);
      }
    },
    [uploadMutation, orderPaymentId, queryClient, toast]
  );

  const deleteFile = useCallback(
    async (ocrJobId: string) => {
      try {
        await deleteMutation.mutateAsync(ocrJobId);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Terjadi kesalahan';
        showToast(toast, 'error', message);
      }
    },
    [deleteMutation, toast]
  );

  const submitProof = useCallback(async () => {
    const validCards = cards.filter((c) => hasManualInputFields(c.state));
    const ocr_job_ids = validCards.map((c) => c.item.id);
    const manual_overrides = form
      .getValues('manual_overrides')
      .filter((o) => ocr_job_ids.includes(o.ocr_job_id));

    const body: SubmitPaymentProofsRequest = {
      ocr_job_ids,
      manual_overrides,
    };

    try {
      await submitMutation.mutateAsync({
        paymentId: orderPaymentId,
        body,
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
        }),
        queryClient.invalidateQueries({
          queryKey: [...FINALIZED_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
        }),
      ]);
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Terjadi kesalahan';
      showToast(toast, 'error', message);
    }
  }, [
    cards,
    form,
    onSuccess,
    orderPaymentId,
    queryClient,
    submitMutation,
    toast,
  ]);

  const resetPollingTimer = useCallback(() => {
    pollingStartRef.current = null;
    setIsPollingTimedOut(false);
  }, []);

  return {
    cards,
    submittedProofs,
    isLoadingItems,
    isLoadingSubmittedProofs,
    isUploading,
    isDeleting,
    isSubmitPending,
    isSubmitting,
    isAnalyzingOcr,
    isPollingTimedOut,
    canSubmit,
    form,
    uploadFiles,
    deleteFile,
    submitProof,
    resetPollingTimer,
  };
};
